import { Suspense } from "react"

import { useDealAssetsMissingMandatoryFields } from "web/hooks/data"
import { Icon } from "web/components"
import { formatList } from "web/services/format"
import routes from "web/routes/constants"

import loadingImg from "web/images/loading.gif"

function Message({ missingStageFields }) {
  const missingMandatoryFields = missingStageFields.filter(
    (stage_field) => stage_field.mandatory
  )

  const numberOfMissingFields = missingMandatoryFields.length
  const missingFieldNames = missingMandatoryFields.map(
    (stageField) => stageField.field.label
  )

  if (numberOfMissingFields === 0) return null

  let missingDetailsMessage

  if (numberOfMissingFields > 5) {
    missingDetailsMessage = `${numberOfMissingFields} fields are required and missing values.`
  } else if (numberOfMissingFields > 1) {
    missingDetailsMessage = `${formatList(missingFieldNames)} are required.`
  } else {
    missingDetailsMessage = `${missingFieldNames[0]} is required.`
  }

  return (
    <div className="mb-5 rounded-md bg-red-50 p-4">
      <div className="flex">
        <div className="flex-shrink-0">
          <Icon name="x-circle" outline className="text-red-700" />
        </div>
        <div className="ml-3">
          <h3 className="text-sm text-red-700">{missingDetailsMessage}</h3>
        </div>
      </div>
    </div>
  )
}

function DealAssetMessage({ dealId, dealAsset }) {
  const missingMandatoryFields = dealAsset.fields.filter(
    (stage_field) => stage_field.mandatory
  )

  const numberOfMissingFields = missingMandatoryFields.length
  const missingFieldNames = missingMandatoryFields.map(
    (stageField) => stageField.field.label
  )

  if (numberOfMissingFields === 0) return null

  let missingDetailsMessage

  if (numberOfMissingFields > 5) {
    missingDetailsMessage = `${numberOfMissingFields} fields are required and missing values.`
  } else if (numberOfMissingFields > 1) {
    missingDetailsMessage = `${formatList(missingFieldNames)} are required.`
  } else {
    missingDetailsMessage = `${missingFieldNames[0]} is required.`
  }

  return (
    <li>
      <a
        className="my-0.5 font-medium text-yellow-800 underline"
        href={`${routes.deal_asset("all", null, dealId, dealAsset.id)}/details`}
        data-turbo-frame="_top"
      >
        {dealAsset.name}
      </a>
      {` - ${missingDetailsMessage}`}
    </li>
  )
}

function MessageContainer({ children }) {
  return (
    <div className="mb-5 rounded-md bg-yellow-50 p-4">
      <div className="flex">
        <div className="flex-shrink-0">
          <Icon
            name="exclamation-triangle"
            outline
            className="text-yellow-700"
          />
        </div>
        <div className="ml-3">{children}</div>
      </div>
    </div>
  )
}

function DealAssetsMessage({ dealId }) {
  const [dealAssetsMissingMandatoryFields] =
    useDealAssetsMissingMandatoryFields(dealId)

  if (dealAssetsMissingMandatoryFields.length === 0) return null

  return (
    <MessageContainer>
      <h3 className="text-sm font-medium text-yellow-800">
        Assets are missing required fields
      </h3>
      <div className="mt-2 text-sm text-yellow-800">
        <ul className="list-disc space-y-1 pl-5">
          {dealAssetsMissingMandatoryFields.map((dealAsset, i) => (
            <DealAssetMessage key={i} dealId={dealId} dealAsset={dealAsset} />
          ))}
        </ul>
      </div>
    </MessageContainer>
  )
}

function LoadingDealAssets() {
  return (
    <MessageContainer>
      <h3 className="flex text-sm font-medium text-yellow-800">
        Checking asset required fields.
        <img src={loadingImg} alt="loading" width="20px" className="ml-2" />
      </h3>
    </MessageContainer>
  )
}

function DealAssetMessageWrapper({ dealId }) {
  return (
    <Suspense fallback={<LoadingDealAssets />}>
      <DealAssetsMessage dealId={dealId} />
    </Suspense>
  )
}

export default function MissingFieldsMessage({
  resourceType,
  id,
  missingStageFields,
}) {
  return (
    <>
      {resourceType === "deal" && <DealAssetMessageWrapper dealId={id} />}
      <Message missingStageFields={missingStageFields} />
    </>
  )
}
